import React, { useState, useEffect, useCallback } from 'react';
import { Grid, GridColumn, GridFilterChangeEvent, GridPageChangeEvent, GridSortChangeEvent } from '@progress/kendo-react-grid';
import { CompositeFilterDescriptor, SortDescriptor } from '@progress/kendo-data-query';
import { getEmployeeContacts } from '../../data/employeeData';
import '../Styles.css';

interface EmployeeContact {
  employeeId: number;
  employeeName: string;
  position: string;
  email: string;
  mobilePhone: string;
}

const Contacts = () => {
  const [data, setData] = useState<EmployeeContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [skip, setSkip] = useState(0);
  const [take, setTake] = useState(25);
  const [total, setTotal] = useState(0);
  const [sort, setSort] = useState<SortDescriptor[]>([{ field: 'employeeName', dir: 'asc' }]);
  const [filter, setFilter] = useState<CompositeFilterDescriptor | undefined>(undefined);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getEmployeeContacts();
      
      // Apply filtering if filter is set
      let filteredData = result;
      if (filter && filter.filters && filter.filters.length > 0) {
        filteredData = result.filter((item: EmployeeContact) => {
          return filter.filters.every((f: any) => {
            if (!f.field || !f.value) return true;
            const itemValue = String(item[f.field as keyof EmployeeContact] || '').toLowerCase();
            const filterValue = String(f.value).toLowerCase();
            return itemValue.includes(filterValue);
          });
        });
      }

      // Apply sorting
      if (sort && sort.length > 0) {
        const sortField = sort[0].field as keyof EmployeeContact;
        const sortDir = sort[0].dir;
        filteredData = [...filteredData].sort((a, b) => {
          const aVal = a[sortField] || '';
          const bVal = b[sortField] || '';
          if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
          if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
          return 0;
        });
      }

      setTotal(filteredData.length);
      setData(filteredData.slice(skip, skip + take));
    } catch (error) {
      console.error('Error loading employee contacts:', error);
    } finally {
      setLoading(false);
    }
  }, [skip, take, sort, filter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handlePageChange = (event: GridPageChangeEvent) => {
    setSkip(event.page.skip);
    setTake(event.page.take);
  };

  const handleSortChange = (event: GridSortChangeEvent) => {
    setSort(event.sort || []);
  };

  const handleFilterChange = (event: GridFilterChangeEvent) => {
    setFilter(event.filter);
    setSkip(0);
  };

  if (loading && data.length === 0) {
    return <div className="loading">Loading contacts...</div>;
  }

  return (
    <div className="p-2">
      <div className="grid__title__container" style={{ marginLeft: '-1rem', marginRight: '-1rem' }}>
        <div className="grid__header__background">
          <div className="grid__title">
            <div>Employee Directory</div>
          </div>
        </div>
      </div>
      
      <Grid
        data={data}
        total={total}
        skip={skip}
        take={take}
        pageable={{ pageSizes: [10, 25, 50, 100] }}
        sortable={true}
        filterable={true}
        sort={sort}
        filter={filter}
        onPageChange={handlePageChange}
        onSortChange={handleSortChange}
        onFilterChange={handleFilterChange}
        style={{ height: 'calc(100vh - 200px)' }}
      >
        <GridColumn
          field="employeeName"
          title="Name"
          width="250px"
        />
        <GridColumn
          field="position"
          title="Position"
          width="200px"
        />
        <GridColumn
          field="email"
          title="Email"
          width="250px"
          cell={(props) => (
            <td>
              {props.dataItem.email ? (
                <a href={`mailto:${props.dataItem.email}`} style={{ color: '#0066cc' }}>
                  {props.dataItem.email}
                </a>
              ) : (
                '-'
              )}
            </td>
          )}
        />
        <GridColumn
          field="mobilePhone"
          title="Mobile Phone"
          width="180px"
          cell={(props) => (
            <td>
              {props.dataItem.mobilePhone ? (
                <a href={`tel:${props.dataItem.mobilePhone}`} style={{ color: '#0066cc' }}>
                  {props.dataItem.mobilePhone}
                </a>
              ) : (
                '-'
              )}
            </td>
          )}
        />
      </Grid>
    </div>
  );
};

export default Contacts;
